import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, CardBody, Stack, H1, Text, Code } from '@neuro/design-system';
import { captureError } from '../utils/sentry';
import { reloadForChunkLoadError } from '../utils/chunkLoadRecovery';
import type { ClassifiedError } from '../types/observability';
import {
  mapBackendErrorToUIError,
  mapAxiosErrorToUIError,
  getCategoryLabel,
  getCategoryIcon,
  getCategoryColor,
  formatErrorTimestamp,
  isClassifiedError,
} from '../utils/error-taxonomy';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Pre-classified error from API response or streaming event */
  classifiedError?: ClassifiedError | null;
  /** Callback when user clicks retry (only shown for retryable errors) */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /** Classified error metadata extracted from the caught error or props */
  classified: ClassifiedError | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI instead of
 * crashing the entire application.
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 * 
 * With custom fallback:
 * ```tsx
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      classified: props.classifiedError ?? null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Attempt to extract classification from the error object
    const anyErr = error as Error & { classified?: ClassifiedError; response?: { data?: unknown } };
    let classified: ClassifiedError | null = null;

    if (anyErr.classified && isClassifiedError(anyErr.classified)) {
      classified = anyErr.classified;
    } else if (anyErr.response?.data) {
      try {
        classified = mapAxiosErrorToUIError(anyErr);
      } catch {
        classified = null;
      }
    }

    return { hasError: true, error, classified };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // If a classifiedError is provided via props, surface it
    if (props.classifiedError && !state.hasError) {
      return { hasError: true, classified: props.classifiedError };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chunk removed by a deploy — reload onto the current build.
    if (reloadForChunkLoadError(error)) return;

    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call the optional error callback
    this.props.onError?.(error, errorInfo);
    
    // Attempt to classify the error if not already done
    let classified = this.state.classified;
    if (!classified) {
      try {
        classified = mapAxiosErrorToUIError(error);
      } catch {
        // fall through — leave classified null
      }
    }

    // Save error info to state for display
    this.setState({
      error,
      errorInfo,
      classified,
    });
    
    // Send error to Sentry with classification metadata
    captureError(error, {
      componentStack: errorInfo.componentStack,
      digest: (errorInfo as ErrorInfo & { digest?: string }).digest,
      ...(classified ? {
        errorCategory: classified.category,
        errorCode: classified.code,
        traceId: classified.traceId,
        spanId: classified.spanId,
      } : {}),
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      classified: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const classified = this.state.classified;

      // ── Structured error UI when classification is available ──
      if (classified) {
        const icon = getCategoryIcon(classified.category);
        const label = getCategoryLabel(classified.category);
        const color = getCategoryColor(classified.category);

        return (
          <Stack align="center" justify="center" style={{ minHeight: '100vh', padding: 'var(--nc-space-6, 1.5rem)' }}>
            <Card padding="lg" style={{ maxWidth: 600, width: '100%', borderLeft: `4px solid ${color}` }}>
              <CardBody>
                <Stack gap={16} align="center">
                  <div style={{ fontSize: '3rem' }} aria-hidden="true">{icon}</div>
                  <H1>{label}</H1>
                  <Text size="xs" color="muted">
                    Code: {classified.code} · {formatErrorTimestamp(classified.timestamp)}
                  </Text>
                  <Text color="secondary" style={{ textAlign: 'center' }}>
                    {classified.message}
                  </Text>

                  {classified.hint && (
                    <Text size="sm" color="secondary" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                      💡 {classified.hint}
                    </Text>
                  )}

                  {classified.retryable && (
                    <Text size="xs" color="muted">This error may be resolved by retrying.</Text>
                  )}

                  {import.meta.env.DEV && (
                    <details style={{ width: '100%', marginTop: 'var(--nc-space-4, 1rem)' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Debug Details</summary>
                      <div style={{ marginTop: 'var(--nc-space-2, 0.5rem)' }}>
                        <Code block>
                          {JSON.stringify(
                            {
                              category: classified.category,
                              code: classified.code,
                              retryable: classified.retryable,
                              traceId: classified.traceId,
                              spanId: classified.spanId,
                              httpStatus: classified.httpStatus,
                            },
                            null,
                            2,
                          )}
                        </Code>
                        {this.state.error && (
                          <>
                            <Text weight="bold" style={{ marginTop: 8 }}>Stack:</Text>
                            <Code block>{this.state.error.toString()}</Code>
                          </>
                        )}
                        {this.state.errorInfo?.componentStack && (
                          <>
                            <Text weight="bold">Component Stack:</Text>
                            <Code block>{this.state.errorInfo.componentStack}</Code>
                          </>
                        )}
                      </div>
                    </details>
                  )}

                  <Stack direction="row" gap={12} justify="center" style={{ marginTop: 'var(--nc-space-4, 1rem)', flexWrap: 'wrap' }}>
                    {classified.retryable && this.props.onRetry && (
                      <Button onClick={this.props.onRetry} variant="primary">
                        🔄 Retry
                      </Button>
                    )}
                    <Button onClick={this.handleReset} variant={classified.retryable ? 'secondary' : 'primary'}>
                      Try Again
                    </Button>
                    <Button onClick={this.handleReload} variant="secondary">
                      Reload Page
                    </Button>
                    <Button onClick={this.handleGoHome} variant="subtle">
                      Go to Home
                    </Button>
                  </Stack>
                </Stack>
              </CardBody>
            </Card>
          </Stack>
        );
      }

      // ── Default (unclassified) error UI ──
      return (
        <Stack align="center" justify="center" style={{ minHeight: '100vh', padding: 'var(--nc-space-6, 1.5rem)' }}>
          <Card padding="lg" style={{ maxWidth: 600, width: '100%' }}>
            <CardBody>
              <Stack gap={16} align="center">
                <div style={{ fontSize: '3rem' }} aria-hidden="true">⚠️</div>
                <H1>Something went wrong</H1>
                <Text color="secondary" style={{ textAlign: 'center' }}>
                  We're sorry, but an unexpected error occurred. The error has been logged
                  and we'll look into it.
                </Text>
                
                {import.meta.env.DEV && this.state.error && (
                  <details style={{ width: '100%', marginTop: 'var(--nc-space-4, 1rem)' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Error Details (Development Mode)</summary>
                    <div style={{ marginTop: 'var(--nc-space-2, 0.5rem)' }}>
                      <Text weight="bold">Error:</Text>
                      <Code block>{this.state.error.toString()}</Code>
                      {this.state.errorInfo?.componentStack && (
                        <>
                          <Text weight="bold">Component Stack:</Text>
                          <Code block>{this.state.errorInfo.componentStack}</Code>
                        </>
                      )}
                    </div>
                  </details>
                )}

                <Stack direction="row" gap={12} justify="center" style={{ marginTop: 'var(--nc-space-4, 1rem)', flexWrap: 'wrap' }}>
                  <Button onClick={this.handleReset} variant="primary">
                    Try Again
                  </Button>
                  <Button onClick={this.handleReload} variant="secondary">
                    Reload Page
                  </Button>
                  <Button onClick={this.handleGoHome} variant="subtle">
                    Go to Home
                  </Button>
                </Stack>
              </Stack>
            </CardBody>
          </Card>
        </Stack>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
